// src/services/nextcloudService.js
// Nextcloud integration: Login Flow v2, WebDAV folder creation & file upload

const NC_REAL_SERVER = "https://nextcloud.malheur-fortuna.local";
// All API requests go through the Vite proxy to avoid CORS
const NC_PROXY = "/nextcloud";

const LS_KEYS = {
  server: "nc_server",
  loginName: "nc_loginName",
  appPassword: "nc_appPassword",
};

// --- Credential Management ---

export const getCredentials = () => {
  const server = localStorage.getItem(LS_KEYS.server);
  const loginName = localStorage.getItem(LS_KEYS.loginName);
  const appPassword = localStorage.getItem(LS_KEYS.appPassword);
  if (server && loginName && appPassword) {
    return { server, loginName, appPassword };
  }
  return null;
};

export const saveCredentials = (server, loginName, appPassword) => {
  localStorage.setItem(LS_KEYS.server, server);
  localStorage.setItem(LS_KEYS.loginName, loginName);
  localStorage.setItem(LS_KEYS.appPassword, appPassword);
};

export const clearCredentials = () => {
  localStorage.removeItem(LS_KEYS.server);
  localStorage.removeItem(LS_KEYS.loginName);
  localStorage.removeItem(LS_KEYS.appPassword);
};

export const isLoggedIn = () => {
  return getCredentials() !== null;
};

// --- Basic Auth Header ---

const authHeaders = (creds) => {
  const encoded = btoa(`${creds.loginName}:${creds.appPassword}`);
  return {
    Authorization: `Basic ${encoded}`,
    "OCS-APIRequest": "true",
  };
};

// --- Rewrite server URL to proxy path ---

/**
 * Replace the real Nextcloud server URL with the proxy prefix.
 * E.g. "https://nextcloud.malheur-fortuna.local/foo" -> "/nextcloud/foo"
 */
const toProxy = (url) => {
  if (url.startsWith(NC_REAL_SERVER)) {
    return url.replace(NC_REAL_SERVER, NC_PROXY);
  }
  return url;
};

// --- Login Flow v2 ---

/**
 * Start Nextcloud Login Flow v2.
 * Opens a browser window for the user to log in.
 * Polls until credentials are returned or timeout.
 * @returns {Promise<{loginName: string, appPassword: string}>}
 */
export const startLogin = async () => {
  // Step 1: Initiate login flow (through proxy)
  const initRes = await fetch(`${NC_PROXY}/index.php/login/v2`, {
    method: "POST",
    headers: { "OCS-APIRequest": "true" },
  });

  if (!initRes.ok) {
    throw new Error(`Login-Initiierung fehlgeschlagen (${initRes.status})`);
  }

  const initData = await initRes.json();
  const { login: loginUrl, poll: pollData } = initData;

  if (!loginUrl || !pollData?.token || !pollData?.endpoint) {
    throw new Error("Ungültige Antwort vom Nextcloud-Server");
  }

  // Step 2: Open login URL in new window (direct URL — user-facing, no CORS)
  window.open(loginUrl, "_blank", "width=600,height=700");

  // Step 3: Poll for completion (rewrite poll endpoint through proxy)
  const pollUrl = toProxy(pollData.endpoint);
  const maxAttempts = 120; // 2 minutes at 1s interval
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const pollRes = await fetch(pollUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "OCS-APIRequest": "true",
        },
        body: `token=${encodeURIComponent(pollData.token)}`,
      });

      if (pollRes.status === 200) {
        const result = await pollRes.json();
        if (result.server && result.loginName && result.appPassword) {
          // Save credentials (store real server, not proxy)
          saveCredentials(result.server, result.loginName, result.appPassword);
          return {
            server: result.server,
            loginName: result.loginName,
            appPassword: result.appPassword,
          };
        }
      }
      // 404 means not yet authenticated, keep polling
      if (pollRes.status !== 404) {
        throw new Error(`Unerwarteter Status: ${pollRes.status}`);
      }
    } catch (err) {
      if (err.message.includes("Unerwarteter Status")) {
        throw err;
      }
    }
  }

  throw new Error("Login-Zeitüberschreitung. Bitte erneut versuchen.");
};

// --- WebDAV Operations ---

// WebDAV base path through proxy
const webdavBase = (creds) =>
  `${NC_PROXY}/remote.php/dav/files/${encodeURIComponent(creds.loginName)}`;

/**
 * Ensure a folder path exists, creating each segment as needed.
 * @param {string} folderPath - e.g. "04_Bibliothek/04_Referenzen/01_Spaces/Zürich/Projekt"
 */
export const ensureFolder = async (folderPath) => {
  const creds = getCredentials();
  if (!creds) throw new Error("Nicht mit Nextcloud verbunden");

  const segments = folderPath.split("/").filter(Boolean);
  let currentPath = "";

  for (const segment of segments) {
    currentPath += `/${segment}`;
    const url = `${webdavBase(creds)}${encodeURI(currentPath)}`;

    const res = await fetch(url, {
      method: "MKCOL",
      headers: authHeaders(creds),
    });

    // 201 = created, 405 = already exists — both are fine
    if (!res.ok && res.status !== 405) {
      if (res.status === 401) {
        throw new Error("Nextcloud-Authentifizierung fehlgeschlagen. Bitte erneut verbinden.");
      }
      throw new Error(`Ordner "${segment}" konnte nicht erstellt werden (${res.status})`);
    }
  }
};

/**
 * Upload a file to Nextcloud via WebDAV PUT.
 * @param {string} remotePath - Full path including filename, e.g. "04_Bibliothek/.../image.jpg"
 * @param {Blob|ArrayBuffer} fileData - The file content
 * @param {string} [contentType] - MIME type
 * @returns {string} The full remote path
 */
export const uploadFile = async (remotePath, fileData, contentType = "application/octet-stream") => {
  const creds = getCredentials();
  if (!creds) throw new Error("Nicht mit Nextcloud verbunden");

  const url = `${webdavBase(creds)}${encodeURI("/" + remotePath)}`;

  const res = await fetch(url, {
    method: "PUT",
    headers: {
      ...authHeaders(creds),
      "Content-Type": contentType,
    },
    body: fileData,
  });

  if (!res.ok) {
    if (res.status === 401) {
      throw new Error("Nextcloud-Authentifizierung fehlgeschlagen. Bitte erneut verbinden.");
    }
    throw new Error(`Upload fehlgeschlagen (${res.status})`);
  }

  return remotePath;
};

// --- High-Level: Upload Reference Image ---

/**
 * Build the Nextcloud folder path for a reference image.
 * Pattern: 04_Bibliothek/04_Referenzen/01_Spaces/{Office}/{Name}
 */
const buildFolderPath = (image) => {
  const office = (image.office || "Unbekannt").trim();
  const name = (image.name || "Unbenannt").trim();
  return `04_Bibliothek/04_Referenzen/01_Spaces/${office}/${name}`;
};

// Azure Blob Storage config for reference images
const BLOB_STORAGE = {
  host: "mfprojects.blob.core.windows.net",
  sasToken:
    "sv=2024-11-04&ss=b&srt=co&sp=rwdlaciytfx&se=2035-10-02T19:07:10Z&st=2025-10-02T10:52:10Z&spr=https&sig=A8jWH2s8LkqmqiQjwjR8bL4sLjDKgKlE1HkDCZSlsYk%3D",
};

/**
 * If the URL points to Azure Blob Storage, append the SAS token.
 */
const withBlobSas = (url) => {
  if (url.includes(BLOB_STORAGE.host)) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${BLOB_STORAGE.sasToken}`;
  }
  return url;
};

/**
 * Fetch an image blob. For Azure Blob Storage URLs the SAS token is
 * appended automatically. Other URLs are tried directly first, then
 * routed through the Vite dev server proxy (/proxy-image) to bypass CORS.
 */
const fetchImageBlob = async (imageUrl) => {
  const url = withBlobSas(imageUrl);

  // Try direct fetch first (works for same-origin / CORS-enabled / SAS URLs)
  try {
    const res = await fetch(url, { mode: "cors" });
    if (res.ok) {
      return await res.blob();
    }
  } catch {
    // CORS blocked — fall through to proxy
  }

  // Route through server-side proxy to bypass CORS
  const proxyRes = await fetch("/proxy-image", {
    headers: { "x-proxy-url": url },
  });
  if (!proxyRes.ok) {
    throw new Error(`Bild konnte nicht geladen werden (${proxyRes.status})`);
  }
  return await proxyRes.blob();
};

/**
 * Upload a single reference image to Nextcloud.
 * Downloads from Azure Blob Storage (originalUrl) or webSourceUrl as fallback.
 * @param {Object} image - Reference image entity
 * @returns {string} The nextcloudPath (remote path)
 */
export const uploadReferenceImage = async (image) => {
  const sourceUrl = image.originalUrl || image.webSourceUrl;
  if (!sourceUrl) {
    throw new Error("Kein Bild-URL verfügbar");
  }

  const folderPath = buildFolderPath(image);
  let fileName = image.fileName || sourceUrl.split("/").pop().split("?")[0] || "image.jpg";
  // Ensure file extension
  if (!/\.\w{2,5}$/.test(fileName)) {
    fileName += ".jpg";
  }
  const remotePath = `${folderPath}/${fileName}`;

  // Ensure folder exists
  await ensureFolder(folderPath);

  // Fetch and upload
  const blob = await fetchImageBlob(sourceUrl);
  await uploadFile(remotePath, blob, blob.type || "image/jpeg");

  return remotePath;
};

/**
 * Upload multiple reference images with progress callback.
 * @param {Array} images - Array of reference image entities
 * @param {Function} onProgress - Callback(current, total, fileName)
 * @returns {{succeeded: Array, failed: Array}}
 */
export const uploadMultipleReferenceImages = async (images, onProgress) => {
  const succeeded = [];
  const failed = [];

  for (let i = 0; i < images.length; i++) {
    const image = images[i];
    const fileName = image.fileName || "Unbekannt";
    if (onProgress) onProgress(i + 1, images.length, fileName);

    try {
      const nextcloudPath = await uploadReferenceImage(image);
      succeeded.push({ image, nextcloudPath });
    } catch (err) {
      console.error(`Nextcloud upload failed for ${fileName}:`, err);
      failed.push({ image, error: err.message });
    }
  }

  return { succeeded, failed };
};

/**
 * Build the Windows local path from a Nextcloud remote path.
 * Nextcloud sync client maps to: C:\Users\{loginName}\M&F\
 * Returns the folder path (without the filename).
 */
export const getLocalPath = (nextcloudPath) => {
  const creds = getCredentials();
  const loginName = (creds?.loginName || "User").replace(/\s/g, "");
  // Strip filename — keep only the folder
  const lastSlash = nextcloudPath.lastIndexOf("/");
  const folderPath = lastSlash > 0 ? nextcloudPath.substring(0, lastSlash) : nextcloudPath;
  return `C:\\Users\\${loginName}\\M&F\\${folderPath.replace(/\//g, "\\")}`;
};

/**
 * Build a file:/// URL to try opening Explorer at the path.
 */
export const getLocalPathAsFileUrl = (nextcloudPath) => {
  const winPath = getLocalPath(nextcloudPath);
  // file:/// URL uses forward slashes
  return `file:///${winPath.replace(/\\/g, "/")}`;
};
